import React, { useEffect, useState } from "react";
import { collection, query, where, getDocs, updateDoc, doc } from "firebase/firestore";
import { Check, Eye, EyeOff, ExternalLink, ShieldAlert, Clock, AlertTriangle } from "lucide-react";

import { db, isAdmin } from "../../firebase.js";
import Button from "../../components/Button.js";
import Message from "../../components/Message.js";

const CATEGORY_LABELS = {
  "muziek-entertainment": "Muziek & entertainment",
  verhuur: "Verhuur",
  "catering-food": "Catering & food",
  "foto-video": "Foto & video",
  vervoer: "Vervoer",
  "styling-decoratie": "Styling & decoratie",
  "overige-diensten": "Overige diensten",
};

// Een abonnement dat niet actief is, betekent: de dienst betaalt niet (meer).
// Een admin die zo'n dienst opnieuw publiceert, zet een niet-betalende
// vermelding online — daarom waarschuwen we daarvoor.
const ACTIVE_SUB_STATUSES = new Set(["active", "trialing"]);

function subscriptionActive(service) {
  return ACTIVE_SUB_STATUSES.has(service.advertentie?.stripeStatus);
}

function ServiceCard({ service, action, pending, onApprove, onUnpublish }) {
  // Niet-gepubliceerde diensten staan nog niet in Algolia; ?preview=1 laat de
  // site ze rechtstreeks uit Firestore tonen.
  const isLive = service.advertentie?.status === "gepubliceerd";
  const detailUrl = `https://feestjegeven.nl/d/${service.slug || "preview"}/${service.id}${isLive ? "" : "?preview=1"}`;
  const publishLabel = action === "republish" ? "Opnieuw publiceren" : "Goedkeuren & publiceren";
  // Bij offline diensten laten we zien of het abonnement nog betaalt, zodat een
  // admin niet per ongeluk een niet-betalende vermelding opnieuw online zet.
  const showSubState = action === "republish";
  const subActive = subscriptionActive(service);
  return (
    <div className="card p-5">
      <div className="flex items-start justify-between gap-4 flex-col sm:flex-row">
        <div className="min-w-0">
          <h3 className="text-base font-heading font-bold truncate">{service.bedrijfsnaam || "(naamloos)"}</h3>
          <p className="text-sm text-text-secondary mt-0.5">
            {CATEGORY_LABELS[service.categorie] || service.categorie || "—"}
            {service.contact?.email ? ` · ${service.contact.email}` : ""}
          </p>
          {showSubState && (
            <p className={`flex items-center gap-1.5 text-sm mt-1 ${subActive ? "text-text-secondary" : "text-warning"}`}>
              {subActive ? (
                "Offline gehaald · abonnement actief"
              ) : (
                <>
                  <AlertTriangle size={14} /> Offline gehaald · abonnement niet actief
                </>
              )}
            </p>
          )}
          {detailUrl && (
            <a
              href={detailUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-accent font-medium hover:underline underline-offset-2 mt-2"
            >
              <ExternalLink size={14} /> Bekijk pagina
            </a>
          )}
        </div>
        <div className="shrink-0">
          {action === "approve" || action === "republish" ? (
            <Button label={publishLabel} icon={Check} onClick={() => onApprove(service)} pending={pending} loadingLabel="Publiceren..." />
          ) : (
            <Button
              variant="secondary"
              label="Depubliceren"
              icon={EyeOff}
              onClick={() => onUnpublish(service)}
              pending={pending}
              loadingLabel="Een momentje..."
            />
          )}
        </div>
      </div>
    </div>
  );
}

function AdminServices() {
  const [loading, setLoading] = useState(true);
  const [pendingList, setPendingList] = useState([]);
  const [onlineList, setOnlineList] = useState([]);
  const [offlineList, setOfflineList] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [message, setMessage] = useState(null);

  const admin = isAdmin();

  async function load() {
    setLoading(true);
    try {
      // Diensten die een admin offline haalt krijgen status "gedepubliceerd"
      // (los van "gepauzeerd", dat een verlopen abonnement betekent en door de
      // subscription-functie wordt gezet). Zonder deze query zouden ze in geen
      // enkele lijst staan en niet meer opnieuw te publiceren zijn. Verlopen
      // ("gepauzeerd") diensten tonen we bewust niet: die betalen niet en horen
      // niet met één klik weer online te kunnen.
      const [pendingSnap, onlineSnap, offlineSnap] = await Promise.all([
        getDocs(query(collection(db, "services"), where("advertentie.status", "==", "te-beoordelen"))),
        getDocs(query(collection(db, "services"), where("published", "==", true))),
        getDocs(query(collection(db, "services"), where("advertentie.status", "==", "gedepubliceerd"))),
      ]);
      setPendingList(pendingSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setOnlineList(onlineSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      // published:true kan in theorie toch een gedepubliceerd-status hebben; die
      // hoort in Online, niet in Offline.
      setOfflineList(offlineSnap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((s) => s.published !== true));
    } catch (err) {
      console.error(err);
      setMessage({ type: "error", text: "Het laden van de diensten is mislukt." });
    }
    setLoading(false);
  }

  useEffect(() => {
    if (admin) load();
    else setLoading(false);
  }, [admin]);

  async function onApprove(service) {
    setBusyId(service.id);
    setMessage(null);
    try {
      await updateDoc(doc(db, "services", service.id), {
        published: true,
        "advertentie.status": "gepubliceerd",
      });
      setMessage({ type: "success", text: `"${service.bedrijfsnaam || service.id}" is goedgekeurd en gaat online.` });
      await load();
    } catch (err) {
      console.error(err);
      setMessage({ type: "error", text: "Goedkeuren is mislukt. Probeer het opnieuw." });
    }
    setBusyId(null);
  }

  async function onUnpublish(service) {
    setBusyId(service.id);
    setMessage(null);
    try {
      // Eigen status voor een admin-actie, los van "gepauzeerd" (dat betekent:
      // abonnement niet meer actief). Zo blijft de dienst zichtbaar in de
      // Offline-lijst en opnieuw te publiceren.
      await updateDoc(doc(db, "services", service.id), {
        published: false,
        "advertentie.status": "gedepubliceerd",
      });
      setMessage({ type: "success", text: `"${service.bedrijfsnaam || service.id}" is offline gehaald.` });
      await load();
    } catch (err) {
      console.error(err);
      setMessage({ type: "error", text: "Depubliceren is mislukt. Probeer het opnieuw." });
    }
    setBusyId(null);
  }

  if (!admin) {
    return (
      <div className="max-w-2xl">
        <Message variant="warning">
          <div className="flex items-center gap-2">
            <ShieldAlert size={18} strokeWidth={1.5} />
            <span>Deze pagina is alleen toegankelijk voor beheerders.</span>
          </div>
        </Message>
      </div>
    );
  }

  return (
    <div className="max-w-3xl">
      <div className="mb-6">
        <h1 className="text-2xl font-heading font-bold">Diensten goedkeuren</h1>
        <p className="text-sm text-text-secondary mt-1">
          Nieuwe diensten komen hier eerst ter controle. Keur ze goed om ze online te zetten.
        </p>
      </div>

      {message && (
        <div className="mb-6">
          <Message variant={message.type} text={message.text} onDismiss={() => setMessage(null)} />
        </div>
      )}

      {loading ? (
        <div className="card p-6 text-center">
          <div className="w-6 h-6 mx-auto mb-3 border-2 border-accent border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-text-secondary">Diensten laden...</p>
        </div>
      ) : (
        <div className="space-y-8">
          <section>
            <div className="flex items-center gap-2 mb-3">
              <Clock size={18} strokeWidth={1.5} className="text-warning" />
              <h2 className="text-lg font-heading font-bold">Wacht op goedkeuring</h2>
              <span className="badge bg-warning-subtle text-warning">{pendingList.length}</span>
            </div>
            {pendingList.length === 0 ? (
              <p className="text-sm text-text-secondary">Geen diensten wachten op goedkeuring.</p>
            ) : (
              <div className="space-y-3">
                {pendingList.map((s) => (
                  <ServiceCard key={s.id} service={s} action="approve" pending={busyId === s.id} onApprove={onApprove} onUnpublish={onUnpublish} />
                ))}
              </div>
            )}
          </section>

          <section>
            <div className="flex items-center gap-2 mb-3">
              <Eye size={18} strokeWidth={1.5} className="text-success" />
              <h2 className="text-lg font-heading font-bold">Online</h2>
              <span className="badge bg-success-subtle text-success">{onlineList.length}</span>
            </div>
            {onlineList.length === 0 ? (
              <p className="text-sm text-text-secondary">Er staan nog geen diensten online.</p>
            ) : (
              <div className="space-y-3">
                {onlineList.map((s) => (
                  <ServiceCard key={s.id} service={s} action="unpublish" pending={busyId === s.id} onApprove={onApprove} onUnpublish={onUnpublish} />
                ))}
              </div>
            )}
          </section>

          <section>
            <div className="flex items-center gap-2 mb-3">
              <EyeOff size={18} strokeWidth={1.5} className="text-text-secondary" />
              <h2 className="text-lg font-heading font-bold">Offline</h2>
              <span className="badge bg-bg-sunken text-text-secondary">{offlineList.length}</span>
            </div>
            {offlineList.length === 0 ? (
              <p className="text-sm text-text-secondary">Geen offline diensten.</p>
            ) : (
              <div className="space-y-3">
                {offlineList.map((s) => (
                  <ServiceCard key={s.id} service={s} action="republish" pending={busyId === s.id} onApprove={onApprove} onUnpublish={onUnpublish} />
                ))}
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}

export default AdminServices;
